import { StoreState } from '@mm/state/store-interfaces';
import { useSelector } from 'react-redux';

export const useDataQualityResults = () => {
  const results = useSelector(
    (state: StoreState) => state.dataQualityTest.results,
  );

  const BAR_HEIGHT = 466;
  const LINE_HEIGHT = 26;

  const calcAccuracy = (matched = 0, mismatched = 0) => {
    const accuracy = (matched / (matched + mismatched)) * 100;
    return Number(accuracy.toFixed(2));
  };

  const calcMatchRate = (matched = 0) => {
    const matchRate = (matched / (results?.total || 0)) * 100;
    return Number(matchRate.toFixed(2));
  };

  const createPercentageArray = () => {
    const maxMatched = (results?.sources || []).reduce(
      (max, obj) => Math.max(max, obj.matched),
      0,
    );
    const maxValue = Math.min(100, calcMatchRate(maxMatched) + 10);
    const percentages = [] as number[];

    for (let i = 0; i <= maxValue; i += 10) {
      percentages.push(i);
    }

    return percentages.sort((a, b) => b - a);
  };

  const calcBarHeight = (maxPercentage = 0, matchedPercentage = 0) => {
    const maxRatio = maxPercentage / 100;
    const mainSourceRatio = matchedPercentage / 100;

    const pixelsPerPercent = BAR_HEIGHT / maxRatio;
    const barHeight = mainSourceRatio * pixelsPerPercent;

    return Math.round(barHeight);
  };

  const calcMatchRateRatio = (
    sourceMatched: number,
    competitorMatched: number,
  ) => {
    const matchRate = calcMatchRate(sourceMatched);
    const competitorMatchRate = calcMatchRate(competitorMatched);
    return (matchRate / competitorMatchRate).toFixed(0);
  };

  const calcMaxMismatch = () => {
    const leastAccurate = (results?.sources || [])
      .filter(c => c.source !== 'xray')
      .reduce(
        (max, obj) => Math.min(max, calcAccuracy(obj.matched, obj.mismatched)),
        100,
      );
    return (100 - (leastAccurate || 0)).toFixed(2);
  };

  return {
    calcMatchRateRatio,
    calcBarHeight,
    createPercentageArray,
    calcMatchRate,
    calcAccuracy,
    results,
    BAR_HEIGHT,
    LINE_HEIGHT,
    maxMismatch: calcMaxMismatch(),
  };
};
